/**
 * Owner: Haselton Baker Risk Group, LLC
 * Product: SP3 - SP3-Ui
 * Developer: Mike McGlone <mike@hbrisk.com>
 * Date: 11/21/17
 * Copyright All Rights Reserved
 */
import React from 'react';
import { Field } from 'redux-form';
import { camelCase, keyBy, map, range } from 'lodash/fp';
import classNames from 'classnames';
import RenderSelect from './RenderSelect';

/**
 * MapTypes
 * @type {Array}
 */
const mapTypesRaw = [
  {
    name: 'soil',
    label: 'Soil',
    default: 13,
    min: 13,
    max: 17,
  },
  {
    name: 'liquefaction',
    label: 'Liquefaction',
    default: 13,
    min: 13,
    max: 17,
  },
  {
    name: 'landslide',
    label: 'Landslide',
    default: 13,
    min: 13,
    max: 17,
  },
  {
    name: 'fault-rupture',
    label: 'Fault Rupture',
    default: 13,
    min: 13,
    max: 17,
  },
  {
    name: 'tsunami',
    label: 'Tsunami',
    default: 13,
    min: 13,
    max: 17,
  },
  {
    name: 'faults',
    label: 'Nearby Faults',
    default: 11,
    min: 11,
    max: 15,
  },
  {
    name: 'historic-shaking',
    label: 'Shake Maps',
    default: 10,
    min: 10,
    max: 14,
  },
  {
    name: 'dam-inundation',
    label: 'Dam Inundation',
    default: 13,
    min: 13,
    max: 17,
  },
];

/**
 * MapTypes
 * @type {Array}
 */
export const mapTypes = map(type => ({
  ...type,
  key: camelCase(type.name),
  inputName: `zoom-${type.name}`,
}), mapTypesRaw);

/**
 * MapTypes
 * @type {Object}
 */
export const mapTypesByKey = keyBy('key', mapTypes);

/**
 * Defines a select menu for an array of zoom levels
 * @constructor
 */
const ZoomLevelSelect = ({ mapType }) => {
  const {
    name,
    default: def,
    min,
    max,
    label,
    inputName,
  } = mapType;
  const zoomOptions = range(min, max + 1)
    .map((value, i) => {
      const description = (value === def) ? 'Normal' : `${i + 1}x`;
      const optionKey = `${name}-${description}`;
      return <option key={optionKey} value={value}>{description}</option>;
    });

  return (
    <Field
      label={label}
      id={inputName}
      name={inputName}
      className={classNames('form-control', 'zoom-level-select')}
      component={RenderSelect}
    >
      {zoomOptions}
    </Field>
  );
};

ZoomLevelSelect.defaultProps = {
  mapType: null,
};

ZoomLevelSelect.propTypes = {
  mapType: (props, propName, componentName) => {
    const mapType = props[propName];
    if (mapType.name === undefined
      || mapType.name === undefined
      || mapType.default === undefined
      || mapType.min === undefined
      || mapType.max === undefined
      || mapType.label === undefined
      || mapType.inputName === undefined) {
      return new Error(`Invalid prop ${propName} supplied to ${componentName}. Validation failed`);
    }
    return null;
  },
};

export default ZoomLevelSelect;
